/**
 * Single onRenderClient for all routes so we always reuse the same root on #root.
 * Prevents "createRoot() on a container that has already been passed to createRoot()" when
 * navigating from a non-SRP page (e.g. job detail) back to the SRP via the logo.
 * Progressive SRP: on SRP we do NOT hydrate; we run the island loader. Other routes hydrate as usual.
 */
import type { PageContextClient } from 'vike/types';
import React from 'react';
import { hydrateRoot, createRoot } from 'react-dom/client';
import { getReactRootSentryOptions } from '../utils/react/react-root-sentry-options';
import type { Root } from 'react-dom/client';
import { Provider } from 'react-redux';
import { VikeReactProviderPageContext } from 'vike-react/usePageContext';
import LayoutDefault from '../layouts/LayoutDefault';
import { createStore } from '../redux/createStore';
import { runSrpIslandLoader, unmountSrpIsland } from './index/srp.client';
import { runJobIslandLoader, unmountJobIsland } from './job/job.client';
import { isStaticDestination } from '../utils/routing/is-static-destination';

export { onRenderClient };

/** Reuse root on client-side navigation so we never call hydrateRoot/createRoot twice on #root. */
let vikeRoot: Root | null = null;
let vikeRootContainer: HTMLElement | null = null;

/** Store root in global so that after client-side nav from a page that used a different chunk, we can still reuse. */
const GLOBAL_ROOT_KEY = '__VIKE_ROOT__';
const GLOBAL_CONTAINER_KEY = '__VIKE_ROOT_CONTAINER__';

function getStoredRoot(): { root: Root; container: HTMLElement } | null {
  if (typeof window === 'undefined') return null;
  const root = (window as any)[GLOBAL_ROOT_KEY];
  const container = (window as any)[GLOBAL_CONTAINER_KEY];
  if (root && container && typeof root.render === 'function') return { root, container };
  return null;
}

function setStoredRoot(root: Root, container: HTMLElement): void {
  if (typeof window === 'undefined') return;
  (window as any)[GLOBAL_ROOT_KEY] = root;
  (window as any)[GLOBAL_CONTAINER_KEY] = container;
}

function clearStoredRoot(): void {
  if (typeof window === 'undefined') return;
  delete (window as any)[GLOBAL_ROOT_KEY];
  delete (window as any)[GLOBAL_CONTAINER_KEY];
}

function isIndexRoute(pageContext: PageContextClient): boolean {
  // On client, always use window.location.pathname for route detection—pageContext can be stale
  // during navigation (e.g. SRP -> /en/resources), causing us to wrongly return early and not update the page.
  const path =
    typeof window !== 'undefined'
      ? window.location.pathname
      : ((pageContext as any)?.urlPathname ?? (pageContext as any)?.urlLogical ?? '/');
  const segments = String(path).split('/').filter(Boolean);
  if (segments.length === 0) return true;
  const [first, second] = [segments[0], segments[1]];
  if (['en', 'fi', 'sv'].includes(first) && segments.length === 1) return true;
  const jobs: Record<string, string> = { en: 'jobs', fi: 'tyopaikat', sv: 'jobb' };
  if (first && jobs[first] === second) return true;
  if (['jobs', 'tyopaikat', 'jobb'].includes(first)) return true;
  return false;
}

function isJobRoute(pageContext: PageContextClient): boolean {
  const path =
    typeof window !== 'undefined'
      ? window.location.pathname
      : ((pageContext as any)?.urlPathname ?? (pageContext as any)?.urlLogical ?? '/');
  return /^\/(en|fi|sv)\/(job|tyopaikka|jobb)\/([^/]+)\/[^/]+(?:\/)?$/.test(String(path))
    || /^\/(job|tyopaikka|jobb)\/([^/]+)\/[^/]+(?:\/)?$/.test(String(path));
}

async function onRenderClient(pageContext: PageContextClient): Promise<void> {
  // Static pages (landing, resource pages) use clientHooks: false — Vike marks them
  // as isHtmlOnly, so the client router treats them as non-SPA-navigable and performs
  // a full browser navigation before ever calling onRenderClient. This block is a
  // belt-and-suspenders fallback for any edge case where Vike still invokes us here.
  // Only reload on a client-side SPA navigation — on initial load the static HTML is
  // already displayed and we must not reload (would loop in dev with Vite HMR).
  if (typeof window !== 'undefined' && isStaticDestination(window.location.pathname)) {
    if (pageContext.isClientSideNavigation) {
      window.location.reload();
    }
    return;
  }

  // 404/error page: always do a hard reload so the server returns the static error page HTML.
  // The _error page uses injectScriptsAt: null and StaticPageLayout — it cannot be client-rendered.
  // This handles all invalid URLs including legacy job URL formats like /fi/job/<base64>.
  if (typeof window !== 'undefined' && (pageContext as any).is404 === true) {
    if (pageContext.isClientSideNavigation) {
      window.location.reload();
    }
    return;
  }

  const root = typeof document !== 'undefined' ? document.getElementById('root') : null;
  const Page = pageContext.Page;
  const Layout = (pageContext as any).Layout ?? LayoutDefault;
  if (!root || !Page) return;

  // Sync from global in case we're in a new chunk and module-level vikeRoot is null (e.g. nav from non-index to index).
  const stored = getStoredRoot();
  if (stored && stored.container === root) {
    vikeRoot = stored.root;
    vikeRootContainer = stored.container;
  }

  const srpSsr = typeof document !== 'undefined' ? document.getElementById('srp-ssr') : null;
  const srpInitial = typeof document !== 'undefined' ? document.getElementById('srp-initial') : null;
  const jobSsr = typeof document !== 'undefined' ? document.getElementById('job-ssr') : null;
  const jobInitial = typeof document !== 'undefined' ? document.getElementById('job-initial') : null;

  // Job detail: static shell then island swap (like SRP). Do not hydrate; run job island.
  if (isJobRoute(pageContext) && jobSsr && jobInitial) {
    runJobIslandLoader(pageContext);
    return;
  }

  // Only run SRP island loader when we're actually on the index route. When navigating from
  // index to another page (e.g. /en/about), the DOM may still have srp-ssr/srp-initial;
  // we must render the new page (with Layout + IntlProvider) instead of returning early.
  if (isIndexRoute(pageContext) && srpSsr && srpInitial) {
    runSrpIslandLoader(pageContext, { mountImmediately: false });
    return;
  }

  // Past this point we're rendering generically (island fast paths above already returned),
  // which means any live SRP/job island is about to have its container ripped out from under it
  // by the vikeRoot render/createRoot below. Unmount those island roots synchronously first so
  // their own unmount effects (e.g. MapView's Mapbox cleanup) run while the container is still
  // attached — otherwise the map is torn out without cleanup and throws when it's eventually
  // unmounted against an already-detached container on a later navigation.
  if (srpSsr && srpInitial) unmountSrpIsland();
  if (jobSsr && jobInitial) unmountJobIsland();

  const layoutTree = Layout
    ? React.createElement(Layout, {}, React.createElement(Page))
    : React.createElement(Page);
  // vike-react's usePageContext()/useData() read from this provider; without it they
  // silently return undefined on the client post-hydration (SSR still works, since
  // vike-react's own onRenderHtml wraps with it — this custom onRenderClient does not).
  const providedTree = React.createElement(VikeReactProviderPageContext, { pageContext }, layoutTree);
  const store = createStore(pageContext);
  const tree = React.createElement(Provider, { store, children: providedTree });

  if (vikeRoot && vikeRootContainer === root) {
    vikeRoot.render(tree);
    if (isJobRoute(pageContext)) {
      requestAnimationFrame(() => {
        const jobApp = document.getElementById('job-app');
        const jobInitial = document.getElementById('job-initial');
        if (jobApp && jobInitial) {
          runJobIslandLoader(pageContext);
        }
      });
    } else if (isIndexRoute(pageContext)) {
      const scheduleIsland = (): void => {
        requestAnimationFrame(() => {
          const ssr = document.getElementById('srp-ssr');
          const initial = document.getElementById('srp-initial');
          if (ssr && initial) {
            runSrpIslandLoader(pageContext, { mountImmediately: true });
          }
        });
      };
      scheduleIsland();
    }
    return;
  }

  if (vikeRoot && vikeRootContainer) {
    vikeRoot.unmount();
    vikeRoot = null;
    vikeRootContainer = null;
    clearStoredRoot();
  }

  if (isIndexRoute(pageContext)) {
    vikeRoot = createRoot(root, getReactRootSentryOptions());
    vikeRoot.render(tree);
    vikeRootContainer = root;
    setStoredRoot(vikeRoot, root);
    const scheduleIsland = (): void => {
      requestAnimationFrame(() => {
        const ssr = document.getElementById('srp-ssr');
        const initial = document.getElementById('srp-initial');
        if (ssr && initial) {
          runSrpIslandLoader(pageContext, { mountImmediately: true });
        }
      });
    };
    scheduleIsland();
    return;
  }

  // When navigating from SRP or job to another page, #root still has that page's HTML (we returned early).
  // Use createRoot to replace the DOM; hydrateRoot would mismatch and can leave the layout broken.
  const comingFromSrp = !!(srpSsr && srpInitial);
  const comingFromJob = !!(jobSsr && jobInitial);
  const rootOpts = getReactRootSentryOptions();
  vikeRoot =
    comingFromSrp || comingFromJob ? createRoot(root, rootOpts) : hydrateRoot(root, tree, rootOpts);
  if (comingFromSrp || comingFromJob) {
    vikeRoot.render(tree);
  }
  vikeRootContainer = root;
  setStoredRoot(vikeRoot, root);
}
